import { QueryClient, useQuery } from '@tanstack/react-query';
import { endpoints } from './index';
import { ApiError } from './errors';
import type { DateView, GameView } from './types';
import { useRealtime } from '@/stores/realtime';

export const qk = {
  config: ['config'] as const,
  catalog: ['catalog'] as const,
  live: ['live'] as const,
  session: (id: string) => ['session', id] as const,
  game: (id: string) => ['game', id] as const,
  connections: ['connections'] as const,
  invites: ['invites'] as const,
  hangouts: ['hangouts'] as const,
  blocks: ['blocks'] as const,
  drops: ['drops'] as const,
  drop: (id: string) => ['drop', id] as const,
  pod: (id: string) => ['pod', id] as const,
  modReports: (status: string) => ['mod', 'reports', status] as const,
  modUser: (id: string) => ['mod', 'user', id] as const,
  metrics: (days: number) => ['admin', 'metrics', days] as const,
  adminFlags: ['admin', 'flags'] as const,
  friendRequests: ['friend-requests'] as const,
  recentPeople: ['people', 'recent'] as const,
  chats: ['chats'] as const,
  billingPlans: ['billing', 'plans'] as const,
  subscription: ['billing', 'subscription'] as const,
  daily: ['daily'] as const,
  /** v6: the session's Date Night view (null = no date). */
  date: (sessionId: string) => ['date', sessionId] as const,
  /** v7: "What Popin has learned" and the admin coverage card. */
  insights: ['me', 'insights'] as const,
  insightsCoverage: ['admin', 'insights', 'coverage'] as const,
  /** v6.1 Popin Dates. */
  dateInvites: ['date-invites'] as const,
  dateInvite: (id: string) => ['date-invite', id] as const,
  invitePreview: (token: string) => ['date-invite-preview', token] as const,
  /** v8 coins & gifts. */
  wallet: ['wallet'] as const,
  walletHistory: ['wallet', 'history'] as const,
  giftCatalog: ['gifts', 'catalog'] as const,
  giftShelf: (dir: string) => ['gifts', 'shelf', dir] as const,
  /** v9 area A. */
  myLink: ['me', 'link'] as const,
  myPhotos: ['me', 'photos'] as const,
  userPhotos: (id: string) => ['users', id, 'photos'] as const,
  linkRequests: ['me', 'link', 'requests'] as const,
  profileViews: ['me', 'profile-views'] as const,
  publicLink: (handle: string) => ['link', handle.toLowerCase()] as const,
  dateSeries: ['date-series'] as const,
};

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10_000,
        refetchOnWindowFocus: false,
        retry: (count, err) => {
          if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/**
 * `GET /v1/config`. Admins switch runtime flags (ads, drops — ARCHITECTURE §22)
 * without a deploy, so the config is refetched every minute while the tab is
 * visible and when the window regains focus (if older than 30 s).
 */
export const CONFIG_REFETCH_MS = 60_000;
export const useConfig = () =>
  useQuery({
    queryKey: qk.config,
    queryFn: endpoints.config,
    staleTime: 30_000,
    refetchInterval: CONFIG_REFETCH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
export const useCatalog = () => useQuery({ queryKey: qk.catalog, queryFn: endpoints.catalog, staleTime: Infinity });
export const useLive = () => useQuery({ queryKey: qk.live, queryFn: endpoints.live, refetchInterval: 15_000 });
export const useSession = (id: string | undefined) =>
  useQuery({ queryKey: qk.session(id ?? ''), queryFn: () => endpoints.session(id!), enabled: !!id });
/** WS `game.state` / `game.effect` keep it fresh; polls a live game only while the WS is down. */
export const useGame = (id: string | null | undefined) => {
  const wsUp = useRealtime((s) => s.status === 'connected');
  return useQuery({
    queryKey: qk.game(id ?? ''),
    queryFn: () => endpoints.game(id!),
    enabled: !!id,
    staleTime: Infinity,
    refetchInterval: (query) => (!wsUp && query.state.data?.status === 'active' ? 3000 : false),
  });
};
export const useConnections = () => useQuery({ queryKey: qk.connections, queryFn: endpoints.connections, refetchInterval: 30_000 });
export const useInvites = (enabled = true) =>
  useQuery({ queryKey: qk.invites, queryFn: endpoints.invites, enabled, refetchInterval: 30_000 });
export const useHangouts = () => useQuery({ queryKey: qk.hangouts, queryFn: endpoints.hangouts });
export const useBlocks = () => useQuery({ queryKey: qk.blocks, queryFn: endpoints.blocks });
export const useDrops = (enabled: boolean) =>
  useQuery({ queryKey: qk.drops, queryFn: endpoints.drops, enabled, refetchInterval: 30_000 });
export const useDrop = (id: string | undefined, enabled = true) =>
  useQuery({ queryKey: qk.drop(id ?? ''), queryFn: () => endpoints.drop(id!), enabled: !!id && enabled, refetchInterval: 15_000 });
export const usePod = (id: string | undefined) =>
  useQuery({ queryKey: qk.pod(id ?? ''), queryFn: () => endpoints.pod(id!), enabled: !!id, refetchInterval: 10_000 });

export const useFriendRequests = (enabled = true) =>
  useQuery({ queryKey: qk.friendRequests, queryFn: endpoints.friendRequests, enabled, refetchInterval: 60_000 });
export const useRecentPeople = () => useQuery({ queryKey: qk.recentPeople, queryFn: endpoints.recentPeople });
export const useChats = (enabled = true) => useQuery({ queryKey: qk.chats, queryFn: endpoints.chats, enabled, refetchInterval: 30_000 });
export const useBillingPlans = (enabled = true) =>
  useQuery({ queryKey: qk.billingPlans, queryFn: endpoints.billingPlans, enabled, staleTime: 60 * 60_000 });
export const useDaily = (enabled = true) => useQuery({ queryKey: qk.daily, queryFn: endpoints.daily, enabled, staleTime: 60_000 });
/** v6.1: my invites as host or guest (WS `date_invite.updated` keeps it fresh). */
export const useDateInvites = (enabled = true) =>
  useQuery({ queryKey: qk.dateInvites, queryFn: endpoints.dateInvites, enabled, refetchInterval: 60_000, retry: false });
export const useSubscription = (enabled = true) => useQuery({ queryKey: qk.subscription, queryFn: endpoints.subscription, enabled });

/** The viewer's IANA zone (for the rhythm card); undefined when unknown. */
export function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}
export const useInsights = () => useQuery({ queryKey: qk.insights, queryFn: () => endpoints.insights(viewerTimeZone()), staleTime: 30_000 });
export const useInsightsCoverage = () => useQuery({ queryKey: qk.insightsCoverage, queryFn: endpoints.insightsCoverage });

/** Only accept a game view that is at least as new as what we hold. */
export function newerGame(prev: GameView | undefined, next: GameView): GameView {
  if (!prev || prev.id !== next.id) return next;
  return next.version >= prev.version ? next : prev;
}

/** Order DateViews by state version; without one (older payloads) or on a tie, by server_time (equal = take the newer arrival). */
export function newerDate(prev: DateView | null | undefined, next: DateView | null): DateView | null {
  if (!next) return prev ?? null;
  if (!prev || prev.id !== next.id) return next;
  if (typeof prev.version === 'number' && typeof next.version === 'number' && prev.version !== next.version) return next.version > prev.version ? next : prev;
  const a = Date.parse(prev.server_time);
  const b = Date.parse(next.server_time);
  if (Number.isNaN(a) || Number.isNaN(b)) return next;
  return b >= a ? next : prev;
}

/** v9 §A1: my personal link (handle null until claimed). */
export const useMyLink = (enabled = true) => useQuery({ queryKey: qk.myLink, queryFn: endpoints.myLink, enabled, retry: false });
export const useMyPhotos = (enabled = true) => useQuery({ queryKey: qk.myPhotos, queryFn: endpoints.myPhotos, enabled, retry: false, staleTime: 10 * 60_000 });
export const useUserPhotos = (id: string | null | undefined) =>
  useQuery({ queryKey: qk.userPhotos(id ?? ''), queryFn: () => endpoints.userPhotos(id!), enabled: !!id, retry: false, staleTime: 10 * 60_000 });
/** v12.1: who opened my link (WS `profile_view.new` keeps it fresh). */
export const useProfileViews = (enabled = true) =>
  useQuery({ queryKey: qk.profileViews, queryFn: endpoints.profileViews, enabled, refetchInterval: 120_000, retry: false });
/** v9 §A1: requests on my link + my own (WS `link_request.updated` keeps it fresh). */
export const useLinkRequests = (enabled = true) =>
  useQuery({ queryKey: qk.linkRequests, queryFn: endpoints.linkRequests, enabled, refetchInterval: 60_000, retry: false });
/** v9 §A5: my weekly date nights (WS `date_series.updated`). */
export const useDateSeries = (enabled = true) =>
  useQuery({ queryKey: qk.dateSeries, queryFn: endpoints.dateSeries, enabled, refetchInterval: 60_000, retry: false });
